import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import {
  ICohort,
  ICohortIndividualSelected,
} from "../../routes/registries/models/Cohort";
import { useDeleteCohortIndividuals } from "../../services/api/cohorts/mutations/useDeleteCohortIndividuals";

interface IProps {
  isOpen: boolean;
  onSuccess: () => void;
  onClose: () => void;
  individuals: ICohortIndividualSelected[];
  cohort: ICohort;
}

export const RemoveFromCohortModal = ({
  onClose,
  onSuccess,
  isOpen,
  individuals,
  cohort,
}: IProps) => {
  const toast = useToast();

  const {
    mutateAsync: mutateAsyncCohortInds,
    isLoading: isLoadingCreateCohortInds,
    isError: isErrorCreateCohortInds,
  } = useDeleteCohortIndividuals();

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSuccess = () => {
    onSuccess();
  };

  const handleOnRemove = async () => {
    const indIds = individuals.map(
      (individual) => individual.cohort_individual_id
    );

    await mutateAsyncCohortInds({
      cohortId: cohort.id,
      cohortIndividuals: indIds,
    });

    toast({
      title: "Removed from Cohort",
      // description: `${individuals.length} individuals added to ${selectedCohortIds.length} cohorts`,
      description: `${individuals.length} cohorts updated`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });

    handleOnSuccess();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      closeOnOverlayClick={true}
      size="3xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove individuals from cohort</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            {individuals?.length && (
              <Stack spacing={8}>
                <Text color="text.softer">
                  Confirm you would like to remove these individuals from{" "}
                  {cohort?.title}
                </Text>

                <Stack spacing={4}>
                  <SimpleGrid columns={2}>
                    <Text textStyle={"label"}>Registry</Text>
                    <Text textStyle={"label"}>Individual Name</Text>
                    {individuals.map((ind, idx) => {
                      return (
                        <React.Fragment key={`${ind.cohort_individual_id}`}>
                          <Text>{ind.registry_name}</Text>
                          <Text>{ind.individual_name}</Text>
                        </React.Fragment>
                      );
                    })}
                  </SimpleGrid>
                </Stack>
              </Stack>
            )}

            {isErrorCreateCohortInds && (
              <Alert status="error" variant={"solid"}>
                <AlertIcon />
                <Text>Failed to remove from cohort, please refresh</Text>
              </Alert>
            )}
          </Stack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup isDisabled={isLoadingCreateCohortInds}>
            <Button onClick={onClose}>Close</Button>
            <Button
              onClick={handleOnRemove}
              colorScheme={"orange"}
              isLoading={isLoadingCreateCohortInds}
            >
              Remove from cohort
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
